import type { GsapTimelineLike, LintFinding, LintSeverity } from './types';
import type { FrameSnapshot } from './measure';
import {
    cssTransformChannels,
    findMatchingClose,
    firstObjectLiteral,
    firstStringArg,
    tweenChannels,
} from './gsapAnalysis';
import {
    textBelowMinSize,
    textBelowRecommendedSize,
    contentOverflowsCanvas,
    contentInSafeMargin,
    textOverlap,
    elementsNearAligned,
} from './geometryRules';
import { textContrastBelowAa } from './contrastRule';
import { CORE_SVG_LINT_RULES } from './svgRules';
import { authoredDocument, authorScriptText } from './authoredSource';
import { textNeverVisible, textReadingTime } from './readingTimeRule';
import { timelineDurationMismatch } from './timelineDurationRule';

/**
 * The loaded composition, for rules that need more than the HTML source — the live DOM and the
 * seekable timeline. `timeline` is null when the composition declares no `window.__timelines.main`.
 */
export interface RuntimeLintContext {
    document: Document;
    timeline: GsapTimelineLike | null;
    /**
     * The composition's source, for rules judging what the *author* wrote: in the loaded `document`,
     * GSAP's inline `transform` pose looks just like an authored declaration. Absent when a caller
     * builds a context by hand (e.g. unit tests), whose `document` is unexecuted source already.
     */
    sourceHtml?: string;
    /**
     * Per-probe frame snapshots (box/opacity/appearance measurements + lazy rasterize) for the
     * render-based geometry and contrast rules. Absent when only the DOM rules run — e.g. unit tests
     * that construct a context by hand — so render-based rules must tolerate an empty/absent list.
     */
    frames?: FrameSnapshot[];
    canvasSize?: { width: number; height: number };
    /**
     * The scene's real on-screen playback length in milliseconds (the codebase's default time unit).
     * Threaded in by the caller — not derivable from the timeline, whose own `.duration()` is the
     * normalized authored length. Rules needing a real duration (reading-time) no-op when it's absent
     * rather than assume one.
     */
    sceneDurationMs?: number;
}

/**
 * The runtime counterpart to the static `LintRule`: it inspects the loaded composition rather than
 * the raw HTML string. The asset runs these after loading the iframe and merges their findings with
 * the static ones into one `LintFinding[]`.
 */
export interface RuntimeLintRule {
    id: string;
    severity: LintSeverity;
    guidance?: string;
    check(context: RuntimeLintContext): string[] | Promise<string[]>;
}

/**
 * A `from`/`fromTo`/`set` that applies explicit values on a transform channel the element's CSS
 * `transform` also provides. GSAP applies those discontinuously and replaces the CSS contribution to
 * that channel — the classic symptom is a translate-centered element jumping by half its size, or
 * ending uncentered after its entrance. `to` tweens are deliberately NOT flagged: they animate from
 * the computed (decomposed) state to an author-chosen end, a legitimate pattern. High precision over
 * recall: only string-literal selectors are resolved, and the AUTHORED CSS transform is read (not
 * getComputedStyle, whose matrix() would erase the translate-vs-scale distinction this rule needs).
 * Authored means `sourceHtml`: `from`/`fromTo`/`set` each write their own `transform` inline before
 * the linter looks, so the loaded document would flag every tween for the pose it wrote itself.
 */
export const transformConflict: RuntimeLintRule = {
    id: 'transform-conflict',
    severity: 'error',
    guidance:
        'Do not put explicit transform channels (x/y/scale/rotation) in a `from`/`fromTo`/`set` on ' +
        'an element that is also positioned with a CSS `transform` — GSAP replaces the CSS ' +
        'contribution and the element jumps. Position with top/left/inset math instead and fold any ' +
        'initial transform state into the tween from-vars. (`to` tweens on a CSS-transformed ' +
        'element are fine.)',
    check(context: RuntimeLintContext): string[] {
        const document = authoredDocument(context);
        const scriptText = authorScriptText(document);
        if (!scriptText) return [];

        // CSS rules that declare `transform` (not transform-origin), kept as selector-list/value
        // pairs. The naive brace split is fine for the flat rules the agent authors.
        const styleText = Array.from(document.querySelectorAll('style'))
            .map((s) => s.textContent || '')
            .join('\n');
        const declRe = /(?:^|[;{\s])transform\s*:\s*([^;}]+)/;
        const transformRules: Array<{ selectors: string[]; value: string }> = [];
        const ruleRe = /([^{}]+)\{([^{}]*)\}/g;
        let rule: RegExpExecArray | null;
        while ((rule = ruleRe.exec(styleText)) !== null) {
            const decl = rule[2].match(declRe);
            if (!decl || decl[1].trim() === 'none') continue;
            transformRules.push({
                selectors: rule[1]
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean),
                value: decl[1].trim(),
            });
        }

        const cssTransformFor = (el: Element): string | null => {
            const inline = (el.getAttribute('style') ?? '').match(declRe);
            if (inline && inline[1].trim() !== 'none') return inline[1].trim();
            for (const r of transformRules) {
                for (const sel of r.selectors) {
                    try {
                        if (el.matches(sel)) return r.value;
                    } catch {
                        // Unparseable selector (at-rule fragment) — skip it.
                    }
                }
            }
            return null;
        };

        const messages: string[] = [];
        const reported = new Set<string>();
        // `to` deliberately absent — see the rule doc comment.
        const callRe = /\b(?:tl|gsap)\.(from|fromTo|set)\s*\(/g;
        let m: RegExpExecArray | null;
        while ((m = callRe.exec(scriptText)) !== null) {
            const argsStart = m.index + m[0].length;
            const argsEnd = findMatchingClose(scriptText, argsStart);
            if (argsEnd === -1) continue;
            const args = scriptText.slice(argsStart, argsEnd);
            callRe.lastIndex = argsEnd + 1;

            const animated = tweenChannels(args);
            if (animated.size === 0) continue;
            const selector = firstStringArg(args);
            if (!selector || reported.has(selector)) continue;

            let elements: Element[];
            try {
                elements = Array.from(document.querySelectorAll(selector));
            } catch {
                continue;
            }
            for (const el of elements) {
                const cssTransform = cssTransformFor(el);
                if (!cssTransform) continue;
                const overlap = [...cssTransformChannels(cssTransform)].filter((c) =>
                    animated.has(c),
                );
                if (overlap.length === 0) continue;
                reported.add(selector);
                messages.push(
                    `"${selector}" carries CSS \`transform: ${cssTransform}\` AND a GSAP ${m[1]} ` +
                        `that applies explicit ${overlap.join('/')} values on the same transform ` +
                        `channel. The explicit values replace the CSS contribution to that channel ` +
                        `(e.g. translate centering is dropped), so the element jumps at build time ` +
                        `or ends displaced after the tween. Position with top/left/inset math ` +
                        `instead and put any initial transform state in the tween from-vars.`,
                );
                break;
            }
        }
        return messages;
    },
};

/**
 * GSAP `from`/`fromTo` apply their FROM state immediately when the timeline is built
 * (immediateRender defaults to true). A tween positioned later than t=0 whose from-state is visible
 * (opacity > 0) therefore flashes the element on from the very start until the tween runs — a common
 * "it was visible the whole time" bug. High precision: only fires on an explicit positive `opacity`
 * in the from-state at an explicit position > 0 with no `immediateRender: false` (the healthy
 * appear-pattern, from {opacity:0}, is correctly left hidden and never fires).
 */
export const gsapImmediateRenderFlash: RuntimeLintRule = {
    id: 'gsap-immediate-render-flash',
    severity: 'error',
    guidance:
        'A `from`/`fromTo` positioned after t=0 with a visible from-state (opacity > 0) renders that ' +
        'state immediately, so the element flashes on at t=0. Add `immediateRender: false`, or ' +
        'gsap.set() the element to its hidden state at build time.',
    check(context: RuntimeLintContext): string[] {
        const scriptText = authorScriptText(context.document);
        if (!scriptText) return [];

        const messages: string[] = [];
        const callRe = /tl\.(from|fromTo)\s*\(/g;
        let m: RegExpExecArray | null;
        while ((m = callRe.exec(scriptText)) !== null) {
            const argsStart = m.index + m[0].length;
            const argsEnd = findMatchingClose(scriptText, argsStart);
            if (argsEnd === -1) continue;
            const args = scriptText.slice(argsStart, argsEnd);
            callRe.lastIndex = argsEnd + 1;

            // Only flag an explicit positive position — chained tweens (no trailing number) stay
            // out of scope to keep the rule high-precision.
            const positionMatch = args.match(/,\s*([\d.]+)\s*$/);
            if (!positionMatch || !(parseFloat(positionMatch[1]) > 0)) continue;
            if (/immediateRender\s*:\s*false/.test(args)) continue;

            const fromObj = firstObjectLiteral(args);
            if (!fromObj) continue;
            const opacityMatch = fromObj.match(/\bopacity\s*:\s*([\d.]+)/);
            if (!opacityMatch || parseFloat(opacityMatch[1]) <= 0) continue;

            const selector = firstStringArg(args);
            const where = selector ? `"${selector}"` : 'an element';
            messages.push(
                `${where} animates from a visible state (opacity ${opacityMatch[1]}) at ` +
                    `${positionMatch[1]}s, but from/fromTo renders that from-state immediately at ` +
                    `t=0 — the element will be visible from the start until the tween runs. Add ` +
                    `\`immediateRender: false\`, or gsap.set() it to its hidden state at build time.`,
            );
        }
        return messages;
    },
};

/**
 * The inverse of gsap-immediate-render-flash: a from/fromTo whose FROM state is hidden (opacity 0)
 * relies on immediateRender — GSAP's default — to apply that hidden state at build time. Passing
 * `immediateRender: false` on such a tween leaves the element at its CSS state (usually fully
 * visible) from the moment it mounts until the tween's position: the viewer sees the text, watches
 * it vanish, then watches it animate "in" a second time. Skips selectors referenced earlier in the
 * script (a gsap.set or prior tween already controls the initial state) and tweens at position ~0
 * (no visible window). Unlike codeo there are no `data-start` clips, so mount time is treated as 0.
 */
export const gsapVisibleBeforeEntrance: RuntimeLintRule = {
    id: 'gsap-visible-before-entrance',
    severity: 'error',
    guidance:
        'Remove `immediateRender: false` from a `from`/`fromTo` whose from-state is hidden ' +
        '(opacity 0) — GSAP’s default immediateRender applies the hidden state at build time so the ' +
        'element stays invisible until its entrance. `immediateRender: false` is only for tweens ' +
        'whose from-state is visible.',
    check(context: RuntimeLintContext): string[] {
        const scriptText = authorScriptText(context.document);
        if (!scriptText) return [];

        const messages: string[] = [];
        const callRe = /tl\.(from|fromTo)\s*\(/g;
        let m: RegExpExecArray | null;
        while ((m = callRe.exec(scriptText)) !== null) {
            const argsStart = m.index + m[0].length;
            const argsEnd = findMatchingClose(scriptText, argsStart);
            if (argsEnd === -1) continue;
            const args = scriptText.slice(argsStart, argsEnd);
            callRe.lastIndex = argsEnd + 1;

            if (!/immediateRender\s*:\s*false/.test(args)) continue;

            const positionMatch = args.match(/,\s*([\d.]+)\s*$/);
            if (!positionMatch) continue;
            const position = parseFloat(positionMatch[1]);

            const fromObj = firstObjectLiteral(args);
            if (!fromObj) continue;
            const opacityMatch = fromObj.match(/\bopacity\s*:\s*([\d.]+)/);
            if (!opacityMatch || parseFloat(opacityMatch[1]) > 0) continue;

            const selector = firstStringArg(args);
            if (!selector) continue;

            // An earlier mention of the selector (gsap.set, a prior tween) means something else
            // already controls the initial state — immediateRender: false is then correct.
            if (scriptText.indexOf(selector) < m.index) continue;

            let el: Element | null = null;
            try {
                el = context.document.querySelector(selector);
            } catch {
                continue;
            }
            if (!el) continue;

            // No clips in the new HtmlAsset: mount time is 0, so a tween starting at ~0 has no
            // visible window before it runs.
            if (position <= 0.05) continue;

            messages.push(
                `"${selector}" tweens from a hidden state (opacity ${opacityMatch[1]}) at ` +
                    `${positionMatch[1]}s with immediateRender: false. The hidden from-state is ` +
                    `never applied early, so the element sits fully visible from mount until the ` +
                    `tween runs, then animates in a second time. Remove \`immediateRender: false\`.`,
            );
        }
        return messages;
    },
};

export const tweenTargetsNothing: RuntimeLintRule = {
    id: 'tween-targets-nothing',
    severity: 'error',
    guidance:
        'Every selector you tween must match an element you actually wrote. GSAP animates an empty match silently — no error, no warning — so an element that exists only in the CSS and the timeline never appears at all. Check `#id` against `id=` and `.class` against `class=` before you finish.',
    check({ document }: RuntimeLintContext): string[] {
        const scriptText = authorScriptText(document);
        if (!scriptText) return [];

        const messages: string[] = [];
        const reported = new Set<string>();
        const callRe = /\.(?:to|from|fromTo|set)\s*\(/g;
        let match: RegExpExecArray | null;
        while ((match = callRe.exec(scriptText)) !== null) {
            const argsStart = match.index + match[0].length;
            const argsEnd = findMatchingClose(scriptText, argsStart);
            if (argsEnd === -1) continue;
            const args = scriptText.slice(argsStart, argsEnd);
            callRe.lastIndex = argsEnd + 1;

            const selector = firstStringArg(args);
            if (!selector || reported.has(selector) || !/^[#.][\w-]+$/.test(selector)) continue;

            let matched: number;
            try {
                matched = document.querySelectorAll(selector).length;
            } catch {
                continue;
            }
            if (matched > 0) continue;

            reported.add(selector);
            const kind = selector.startsWith('#') ? 'id' : 'class';
            const other = selector.startsWith('#')
                ? `class="${selector.slice(1)}"`
                : `id="${selector.slice(1)}"`;
            messages.push(
                `"${selector}" is tweened but matches no element, so that tween animates nothing ` +
                    `and the element never appears. Either the markup is missing it, or it exists ` +
                    `with ${other} — an ${kind} selector does not match the other attribute. Add ` +
                    `the element, or target it by what it actually carries.`,
            );
        }
        return messages;
    },
};

export const CORE_RUNTIME_LINT_RULES: RuntimeLintRule[] = [
    tweenTargetsNothing,
    transformConflict,
    gsapImmediateRenderFlash,
    gsapVisibleBeforeEntrance,
    textBelowMinSize,
    textBelowRecommendedSize,
    contentOverflowsCanvas,
    contentInSafeMargin,
    textOverlap,
    elementsNearAligned,
    textContrastBelowAa,
    textNeverVisible,
    textReadingTime,
    timelineDurationMismatch,
    ...CORE_SVG_LINT_RULES,
];

/**
 * Run the runtime rules against a loaded composition. A rule's `check` may be synchronous (most
 * rules, which inspect the already-loaded DOM) or asynchronous (e.g. contrast, which rasterizes
 * frames); the results are awaited either way and merged into one finding list.
 */
export async function runRuntimeLintRules(
    context: RuntimeLintContext,
    rules: RuntimeLintRule[] = CORE_RUNTIME_LINT_RULES,
): Promise<LintFinding[]> {
    const findings: LintFinding[] = [];
    for (const rule of rules) {
        const messages = await rule.check(context);
        for (const message of messages) {
            findings.push({
                ruleId: rule.id,
                severity: rule.severity,
                message,
            });
        }
    }
    return findings;
}
